import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { Database } from "@/integrations/supabase/types";

type SubscriptionPlan = Database["public"]["Enums"]["subscription_plan"];
type BillingPeriod = Database["public"]["Enums"]["billing_period"];

export interface Subscription {
  id: string;
  plan: SubscriptionPlan;
  status: string;
  credits_per_period: number;
  billing_period: BillingPeriod | null;
  trial_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  paypal_subscription_id: string | null;
}

export interface PlanDetails {
  id: SubscriptionPlan;
  name: string;
  credits: number | "unlimited";
  priceMonthly: number;
  priceYearly: number;
  features: string[];
  bulkLimit: number; // Max URLs per bulk analysis
  crawlLimit: number; // Max URLs discovered per crawl
}

type LangKey = 'fr' | 'en' | 'es';

interface PlanTexts {
  name: Record<LangKey, string>;
  features: Record<LangKey, string[]>;
}

const PLAN_TEXTS: Record<string, PlanTexts> = {
  free: {
    name: { fr: "Gratuit", en: "Free", es: "Gratis" },
    features: {
      fr: ["3 analyses par jour", "5 crédits offerts à l'inscription (sans expiration)", "Avec vos crédits : jusqu'à 5 pages d'un coup et exploration de site", "Schemas JSON-LD", "Export du code"],
      en: ["3 analyses per day", "5 bonus credits at signup (never expire)", "With your credits: up to 5 pages at once and site exploration", "JSON-LD Schemas", "Code export"],
      es: ["3 análisis por día", "5 créditos de regalo al registrarte (sin caducidad)", "Con tus créditos: hasta 5 páginas a la vez y exploración de sitio", "Schemas JSON-LD", "Exportar código"],
    },
  },
  standard: {
    name: { fr: "Standard", en: "Standard", es: "Estándar" },
    features: {
      fr: ["200 analyses par mois", "Jusqu'à 10 URLs en lot", "Crawl jusqu'à 50 pages", "Schemas JSON-LD", "Export du code", "Historique complet", "Accès API MCP (1 clé)", "Support prioritaire"],
      en: ["200 analyses/month", "Up to 10 URLs in bulk", "Crawl up to 50 pages", "JSON-LD Schemas", "Code export", "Full history", "MCP API access (1 key)", "Priority support"],
      es: ["200 análisis/mes", "Hasta 10 URLs en lote", "Crawl hasta 50 páginas", "Schemas JSON-LD", "Exportar código", "Historial completo", "Acceso API MCP (1 clave)", "Soporte prioritario"],
    },
  },
  pro: {
    name: { fr: "Pro", en: "Pro", es: "Pro" },
    features: {
      fr: ["1000 analyses par mois", "Jusqu'à 20 URLs en lot", "Crawl jusqu'à 500 pages", "Schemas JSON-LD", "Export du code", "Historique complet", "Accès API MCP (1 clé)", "🔔 Monitoring hebdomadaire", "Support prioritaire"],
      en: ["1,000 analyses/month", "Up to 20 URLs in bulk", "Crawl up to 500 pages", "JSON-LD Schemas", "Code export", "Full history", "MCP API access (1 key)", "🔔 Weekly monitoring", "Priority support"],
      es: ["1.000 análisis/mes", "Hasta 20 URLs en lote", "Crawl hasta 500 páginas", "Schemas JSON-LD", "Exportar código", "Historial completo", "Acceso API MCP (1 clave)", "🔔 Monitoreo semanal", "Soporte prioritario"],
    },
  },
  unlimited: {
    name: { fr: "Agence", en: "Agency", es: "Agencia" },
    features: {
      fr: ["5000 analyses par mois", "Jusqu'à 50 URLs en lot", "Crawl jusqu'à 10 000 pages", "Schemas JSON-LD", "Export du code", "Historique complet", "Accès API MCP (5 clés)", "🔔 Monitoring hebdomadaire", "Support prioritaire"],
      en: ["5,000 analyses/month", "Up to 50 URLs in bulk", "Crawl up to 10,000 pages", "JSON-LD Schemas", "Code export", "Full history", "MCP API access (5 keys)", "🔔 Weekly monitoring", "Priority support"],
      es: ["5.000 análisis/mes", "Hasta 50 URLs en lote", "Crawl hasta 10.000 páginas", "Schemas JSON-LD", "Exportar código", "Historial completo", "Acceso API MCP (5 claves)", "🔔 Monitoreo semanal", "Soporte prioritario"],
    },
  },
};

const PLANS_BASE: Omit<PlanDetails, 'name' | 'features'>[] = [
  { id: "free", credits: 3, priceMonthly: 0, priceYearly: 0, bulkLimit: 1, crawlLimit: 0 },
  { id: "standard", credits: 200, priceMonthly: 9, priceYearly: 92, bulkLimit: 10, crawlLimit: 50 },
  { id: "pro", credits: 1000, priceMonthly: 39, priceYearly: 398, bulkLimit: 20, crawlLimit: 500 },
  { id: "unlimited", credits: 5000, priceMonthly: 89, priceYearly: 908, bulkLimit: 50, crawlLimit: 10000 },
];

export const getLocalizedPlans = (lang: LangKey = 'fr'): PlanDetails[] =>
  PLANS_BASE.map((base) => {
    const texts = PLAN_TEXTS[base.id];
    return {
      ...base,
      name: texts.name[lang],
      features: texts.features[lang],
    };
  });

/** @deprecated Use getLocalizedPlans(lang) instead */
export const PLANS: PlanDetails[] = getLocalizedPlans('fr');

export function useSubscription() {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!user?.id) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (fetchError) {
        console.error("Error fetching subscription:", fetchError);
        setError(fetchError.message);
        setSubscription(null);
      } else {
        setSubscription(data);
        setError(null);
      }
    } catch (err) {
      console.error("Error in fetchSubscription:", err);
      setError("Failed to fetch subscription");
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const getCurrentPlan = (): PlanDetails | undefined => {
    if (!subscription) return PLANS[0]; // Default to free
    return PLANS.find((p) => p.id === subscription.plan);
  };

  const isTrialActive = (): boolean => {
    if (!subscription?.trial_ends_at) return false;
    return new Date(subscription.trial_ends_at) > new Date();
  };

  const getTrialDaysRemaining = (): number => {
    if (!subscription?.trial_ends_at) return 0;
    const trialEnd = new Date(subscription.trial_ends_at);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  };

  return {
    subscription,
    loading,
    error,
    currentPlan: getCurrentPlan(),
    isTrialActive: isTrialActive(),
    trialDaysRemaining: getTrialDaysRemaining(),
    refetch: fetchSubscription,
    isAuthenticated,
  };
}
